import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import {
  AUTH_LOGIN_PROVIDERS,
  AuthProviderLoginOption,
  ProviderKey,
} from '../auth/auth.config';
import { AuthService } from '../auth/auth.service';
import { ButtonComponent } from '../components/button/button.component';
import { FeatureFlagService } from '../services/feature-flag.service';

@Component({
  standalone: true,
  selector: 'app-login',
  imports: [CommonModule, ButtonComponent],
  template: `
    <div class="screen">
      <section class="left">
        <img
          src="assets/svg/QS_New_Logo_Black.svg"
          alt="Company Logo"
          class="brand"
        />
      </section>
      <section class="right">
        <div class="card">
          <h1 data-testid="login-welcome">Welcome!</h1>
          <span class="subtitle">
            <span>Pick a login option to continue to get</span>
            <span>QS Connected.</span>
          </span>

          <span class="btn-area">
            @for (provider of providers; track provider.key) {
              <span class="provider">
                <app-button
                  [testId]="'login-provider-' + provider.key"
                  (click)="login(provider.key)"
                  [title]="provider.title"
                  [tall]="true"
                  [disabled]="!(provider.enabled$ | async) || busy"
                >
                </app-button>
                <img
                  [src]="provider.logoSrc"
                  [alt]="provider.logoAlt"
                  class="provider-logo"
                />
              </span>
            }
          </span>
        </div>
      </section>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .screen {
        min-height: 100vh;
        width: 100%;
        display: flex;
        background:
          radial-gradient(
            circle at center right,
            rgba(255, 255, 255, 0.08) 0%,
            transparent 60%
          ),
          linear-gradient(90deg, #0f0f0f 0%, #2f2f2f 100%);
      }

      .left,
      .right {
        flex: 1 1 50%;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .provider {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .provider-logo {
        width: 28px;
        height: 28px;
        object-fit: contain;
      }
      .right {
        padding: 2rem;
      }

      .card {
        width: 100%;
        max-width: 396px;
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
        padding: 28px;
      }

      .card h1 {
        margin: 0 0 8px;
        font-size: 44px;
        line-height: 1.2;
        color: #111;
        letter-spacing: 0.2px;
        font-weight: 700;
      }

      .card .subtitle {
        display: flex;
        flex-direction: column;
        font-size: 18px;
        line-height: 1.4;
        color: #555;
        margin-bottom: 32px;
      }
      .btn-area {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 12px;
      }

      .brand {
        width: 46%;
        max-width: 520px; /* make it nice and large */
        min-width: 280px;
        height: auto;
        opacity: 0.9;
        filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.5));
      }

      /* Big screens: give the logo even more presence */
      @media (min-width: 1400px) {
        .brand {
          max-width: 620px;
        }
      }

      /* Mobile: stack vertically */
      @media (max-width: 900px) {
        .screen {
          flex-direction: column;
        }
        .left,
        .right {
          width: 100%;
        }
        .brand {
          width: 40vw;
          max-width: 420px;
          margin: 40px 0;
        }
        .card {
          margin: 24px 0;
        }
      }
    `,
  ],
})
export class LoginComponent {
  private auth = inject(AuthService);
  private router = inject(Router);
  public featureFlagService = inject(FeatureFlagService);

  providers: (AuthProviderLoginOption & { enabled$: Observable<boolean> })[] =
    AUTH_LOGIN_PROVIDERS.map((provider) => ({
      ...provider,
      enabled$: provider.featureFlag
        ? this.featureFlagService.isFeatureEnabled(provider.featureFlag)
        : of(true),
    }));

  busy = false;

  ngOnInit() {
    if (this.auth.isAuthenticated()) {
      const target = sessionStorage.getItem('post_login_redirect') || '/';
      this.router.navigateByUrl(target);
    }
  }

  async login(provider: ProviderKey) {
    if (this.busy) return;
    this.busy = true;

    try {
      await this.auth.setProvider(provider);
      const url = sessionStorage.getItem('post_login_redirect') || '/';
      await this.auth.login(url);
    } finally {
      this.busy = false;
    }
  }
}
